/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { makeStyles } from '@material-ui/core';
import {
    shape, string, func, bool,
    object,
} from 'prop-types';
import { bindActionCreators } from 'redux';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { trackEvent } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import SiteWideBannerUI from './SiteWideBannerUI';
import useUTMRules from '../../../../helpers/DynamicRules/hooks/useUTMRules';
import useUserType from './UseUserType';
import isAfterCutoff from '../../../../helpers/determineCutoffTime';
import LinkOrATag from '../../GraphqlCommonComponents/LinkOrATag/LinkOrATag';
import SiteWideBannerMultiOffer from './SiteWideBannerMultiOffer/SiteWideBannerMultiOffer';
import OfferGatewaySiteWideUI from './OfferGatewaySiteWideUI';
import useOfferGatewayClickHandler from '../../GraphqlCommonComponents/GenericDialog/OfferGateway/useOfferGatewayClickHandler';

const useStyles = makeStyles(() => ({
    link: {
        textDecoration: 'none',
    },
}));

const SiteWideBannerContainer = ({
    bannerData, track, trackClickThrough, isFromPersonalizedSiteWideBanner, isMultiOfferEnabledCms, isOfferGatewayEnabled, salesforceResponse,
}) => {
    const showUserType = bannerData?.show_to_user_types || [];
    const enableSiteWideBanner = useUserType(showUserType);
    const isLink = bannerData?.linking?.link?.href.length;
    const cutoffWindow = bannerData?.cutoff_window;
    const classes = useStyles();
    const link = bannerData?.linking?.link;
    const ga = bannerData?.linking;

    const cutoffHandlers = {
        show_before: () => !isAfterCutoff(),
        show_after: () => isAfterCutoff(),
        show_always: () => true,
    };

    const campaignPayload = salesforceResponse.campaign?.campaignResponses?.[0]?.payload || {};
    const isOfferUnlocked = campaignPayload.offerFlow === 'Offer Unlocked' || campaignPayload.offerFlow === 'Offer Unlocked - SMS';
    const customButtons = salesforceResponse?.content?.custom_button;
    const lockButtons = customButtons?.find((item) => item?.offer_status === 'lock');
    const unlockButtons = customButtons?.find((item) => item?.offer_status === 'unlock' || item?.offer_status === 'copy');

    if (isOfferUnlocked && (campaignPayload?.offerEndState === 'Collection Page' && unlockButtons?.cta_button?.link?.href && campaignPayload?.collectionURL)) {
        if (campaignPayload?.offerBannerCode) {
            unlockButtons.cta_button.link.href = `${campaignPayload.collectionURL}?r=${campaignPayload.offerBannerCode}`;
        } else {
            unlockButtons.cta_button.link.href = campaignPayload.collectionURL;
        }
    }

    const buttonToShow = isOfferUnlocked ? unlockButtons : lockButtons;

    const { handleSiteWideBannerClick } = useOfferGatewayClickHandler(salesforceResponse);

    const isCutOffEnable = (cutoffHandlers[cutoffWindow] || (() => true))();
    const utmPassed = useUTMRules(bannerData);
    if (!utmPassed) {
        return <></>;
    }
    const shouldShowMultiOffer =  isMultiOfferEnabledCms && enableSiteWideBanner;
    const shouldShowSingleBanner = enableSiteWideBanner && isCutOffEnable && !isMultiOfferEnabledCms;

    const handleTracking = (cat, act, lab) => {
        track({
            eventCategory: cat || '',
            eventAction: act || '',
            eventLabel: lab || '',
        });
        if (isFromPersonalizedSiteWideBanner) trackClickThrough();
    };

    if (shouldShowMultiOffer) {
        return <SiteWideBannerMultiOffer />;
    }
    if (isOfferGatewayEnabled) {
        return (
            <div data-testid="og-bannerv2">
                <LinkOrATag
                    className={classes.link}
                    href={buttonToShow?.cta_button?.link?.href || '#'}
                    title={buttonToShow?.cta_button?.link?.href}
                    isTrackEventEnabled
                    trackingEventCategory={buttonToShow?.cta_button?.tracking?.tracking_event_category || ''}
                    trackingEventAction={buttonToShow?.cta_button?.tracking?.tracking_event_action || ''}
                    trackingEventLabel={buttonToShow?.cta_button?.tracking?.tracking_event_label || ''}
                    handleCallback={() => handleSiteWideBannerClick(buttonToShow)}
                >
                    <OfferGatewaySiteWideUI bannerData={bannerData} clickHandler={handleTracking} salesforceResponse={salesforceResponse} />
                </LinkOrATag>
            </div>
        );
    }

    return (
        shouldShowSingleBanner
        && (
            <div data-testid="bannerv2">
                {isLink ? (
                    <LinkOrATag
                        className={classes.link}
                        href={link.href}
                        title={link.title}
                        isTrackEventEnabled
                        trackingEventCategory={ga.tracking_event_category || ''}
                        trackingEventAction={ga.tracking_event_action || ''}
                        trackingEventLabel={ga.tracking_event_label || ''}
                    >
                        <SiteWideBannerUI bannerData={bannerData} clickHandler={handleTracking} />
                    </LinkOrATag>
                ) : (
                    <SiteWideBannerUI bannerData={bannerData} clickHandler={handleTracking} />
                )}
            </div>
        )
    );
};

SiteWideBannerContainer.defaultProps = {
    bannerData: {},
    isFromPersonalizedSiteWideBanner: false,
    trackClickThrough: () => { },
    isMultiOfferEnabledCms: false,
    isOfferGatewayEnabled: false,
    salesforceResponse: {},
};

SiteWideBannerContainer.propTypes = {
    bannerData: shape({
        about_the_banner_message_: string,
        message: string,
        title: string,
        styling: shape({
            background_color: shape({
                color: string,
            }),
            cta_font: string,
        }),
    }),
    track: func.isRequired,
    isFromPersonalizedSiteWideBanner: bool,
    trackClickThrough: func,
    isMultiOfferEnabledCms: bool,
    isOfferGatewayEnabled: bool,
    salesforceResponse: object,
};
const mapDispatchToProps = (dispatch) => ({
    track: bindActionCreators(trackEvent, dispatch),
});
const enhance = compose(
    connect(null, mapDispatchToProps),
);
export default enhance(SiteWideBannerContainer);
